import React, {useEffect, useMemo} from 'react';

import {
  Alert,
  Button,
  Fade,
  Skeleton,
  SvgIcon,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Box,
  Stack,
} from '@zoom/react-ui';
import {isToday} from 'date-fns';
import {DateTime} from 'luxon';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';

// eslint-disable-next-line no-unused-vars
import {DesktopSlot, RenderSlotProps} from './DesktopSlot.tsx';
import {
  TimeSelectSection,
  Slots,
  SlotsHeaderDate,
  SlotsHeaderDayName,
  SlotDivider,
} from './Style.js';

import CalendarIllustrationGray from 'assets/svg/calendar-illustration-gray.svg';
import {ReactComponent as SunOutlined} from 'assets/svg/sun-outlined.svg';
import {TypographyBody3} from 'Components/Appointments/Style.js';
import {toDateTime} from 'Components/BookingBlocksWidget/utils.js';
import {getI18nLanguage} from 'i18n';
import {
  selectWeekAvailability,
  setCurrentTimeCutoff,
} from 'Store/BookAppointmentsStore.js';
import {
  getAvailableSlots,
  getCushionedTimesForDay,
} from 'Utils/apptAvailabilityUtils';
import {AVAIL_STATUS, HOUR_MINUTES} from 'Utils/consts.js';
import DateWithTimeZone from 'Utils/DateWithTimeZone';
import {useCushionCutoff} from 'Utils/useDateHooks.js';
import zoomPalette from 'zoomPalette.js';

import 'react-datepicker/dist/react-datepicker.css';


// Return true if the start time and the end time of an appointment slot are in different timezones
// This can happen on a daylight saving time (DST) changing day
// e.g. on Sunday, Nov 6th 2022, DST ends at 2:00 AM (clocks are set back one hour)
// An appointment can start from 1:45 PDT to 1:15 PST (30 minutes duration)
const crossedTimezone = (slot, durationMinutes, timeZone) => {
  const slotStart = toDateTime(slot.start, timeZone);
  const slotEnd = slotStart.plus({minutes: durationMinutes || 0});

  if (slotStart.offset !== slotEnd.offset) {
    return true;
  }
  return false;
};

/**
 * Booking Time Slot Selector Component
 * @param {Object} props
 * @param {Date} props.slotsForDate
 * @param {{
 *  start: {dateTime: string, timeZone: string}, end: {dateTime: string, timeZone: string}
 * } | undefined} props.bookedSlot the booked event data that is being rescheduled; if any
 * @param {React.FC<RenderSlotProps>|undefined} props.RenderSlot
 * @return {ReactElement}
 */
export default function SlotSelector(props) {
  const {
    // currentTimeCutoff,
    RenderSlot = DesktopSlot,
    bookedSlot,
    displayLongHeader,
    durationMinutes,
    hasTroubleShootingMode = false,
    hideHeader,
    // the selected date picker date
    hideHeaderDate,
    isBookerVoting,
    isLoading,
    onSelectSlot,
    selectedDate,
    selectedSlot,
    selectedSlots,
    slotProps,
    slotsForDate,
    timeZone,
    troubleShootingMode,
    voterInfo,
  } = props;
  const {t} = useTranslation();

  const voterEmail = voterInfo?.email || '';

  const dispatch = useDispatch();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const timeFormat = useSelector((state) => state.hostSettingsState?.timeFormat?.value);

  const slotsForDt = useMemo(() => toDateTime(slotsForDate, timeZone, true).setLocale(getI18nLanguage())
    , [slotsForDate, timeZone]);

  const appt = useSelector((s) => s.bookAppointments.appt);

  const isLoadingInstancesOrDetails = isLoading;

  const bookedSlotStart = new DateWithTimeZone(bookedSlot?.start?.dateTime);

  const apptDaysData = useSelector((s) => {
    return s.bookAppointments.apptDays;
  });
  const availableSlots = getAvailableSlots(appt, apptDaysData, slotsForDate, timeZone);

  const currentTimeCutoff = useCushionCutoff(appt, availableSlots.map((slot) => slot.start));
  const currentTimeCutoffISO = currentTimeCutoff.toISOString();

  useEffect(() => {
    dispatch(setCurrentTimeCutoff(currentTimeCutoffISO));
  }, [dispatch, currentTimeCutoffISO]);

  const cushionedAvailableTimes =
    isBookerVoting || troubleShootingMode ?
      availableSlots :
      getCushionedTimesForDay(
        availableSlots,
        currentTimeCutoff,
        bookedSlotStart,
      );

  const hasErrorLoadingTimes = useSelector((s) => selectWeekAvailability(s, slotsForDt, timeZone))?.error;

  /**
   * Helper function to display the Skeleton
   * @return {Object}
   */
  function iterateSkeleton() {
    const arr = [];
    for (let i = 0; i < 3; i++) {
      arr.push(<Skeleton
        key={`${i}-1`}
        variant="rounded"
        animation="wave"
        height={32}
        sx={{
          width: '90%',
          bgcolor: zoomPalette.gray[30],
          marginBottom: '8px',
          borderRadius: '8px',
        }} />);
      arr.push(<Skeleton
        key={`${i}-2`}
        variant="rounded"
        animation="wave"
        height={32}
        sx={{
          width: '60%',
          bgcolor: zoomPalette.gray[20],
          marginBottom: '8px',
          borderRadius: '8px',
        }} />);
      arr.push(<Skeleton
        key={`${i}-3`}
        variant="rounded"
        animation="wave"
        height={32}
        sx={{
          width: '30%',
          bgcolor: zoomPalette.gray[40],
          marginBottom: '8px',
          borderRadius: '8px',
        }} />);
    }

    return (
      <Stack
        sx={{
          width: '100%',
          marginTop: '2rem',
        }}
      >
        {arr}
      </Stack>
    );
  }

  const isNoAvailability = (cushionedAvailableTimes.length === 0 && !troubleShootingMode) ||
    (availableSlots.length === 0 && troubleShootingMode);

  return (
    <TimeSelectSection>
      <Stack
        direction="column"
        flexWrap="nowrap"
        alignItems="center"
        flexShrink={1}
        textAlign="center"
        height="100%"
      >
        {slotsForDate && (
          <>
            {!hideHeader && <SlotsHeaderDayName>
              {displayLongHeader ?
                slotsForDt.toFormat('DDDD') :
                slotsForDt.toFormat('EEEE')}
            </SlotsHeaderDayName>}
            {!hideHeader && (!isSmallScreen ?
              (!hideHeaderDate && (
                <SlotsHeaderDate
                  isSelected={new Date(selectedDate).valueOf() === slotsForDate.valueOf()}
                  isToday={isToday(slotsForDate)}
                >
                  {slotsForDt.toFormat('d').trim()}
                </SlotsHeaderDate>
              )) :
              <div
                style={{
                  fontWeight: '400',
                  fontSize: '1rem',
                  lineHeight: '1.25rem',
                  color: zoomPalette.gray['60'],
                  textAlign: 'center',
                }}
              >
                {slotsForDt.toFormat('DDD').trim()}
                {isToday(slotsForDate) && ` (${t('common.today')})`}
              </div>)
            }
            <div
              style={{
                flexGrow: 1,
                overflowY: 'auto',
                height: '100%',
                padding: '0px 4px',
                width: '100%',
              }}
            >
              {(isLoadingInstancesOrDetails || hasErrorLoadingTimes) ?
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '100px',
                  }}
                >
                  {isLoadingInstancesOrDetails ?
                  iterateSkeleton() :
                    <Alert
                      severity="error"
                      variant="standard"
                      sx={{
                        borderRadius: 3,
                        alignSelf: 'start',
                        maxWidth: 'fit-content',
                        marginBottom: 2,
                      }}
                    >
                      Failed to get availability, please try again later.
                    </Alert>
                  }
                </Box> :
                <Fade in={true}>
                  <Slots className="slots" sx={{...slotProps?.slotsContainer?.sx}} >
                    {(cushionedAvailableTimes).map((slot, i) => {
                      const id = `${slotsForDate.toDateString()}-${slot.start.toTimeString()}`;
                      const ariaLabel = `Select slot on ${toDateTime(slot.start, timeZone)
                        .toLocaleString(DateTime.DATETIME_SHORT_WITH_SECONDS)}`;
                      // detect if we have crossed a daylight savings boundary
                      let offsetDifference = 0;
                      if (i > 0) {
                        offsetDifference = toDateTime(cushionedAvailableTimes[i-1]?.start, timeZone).offset -
                            toDateTime(slot.start, timeZone).offset;
                      }
                      const showDstDivider = (offsetDifference !== 0);
                      const showDivider = (
                        !showDstDivider &&
                          i > 0 &&
                          (cushionedAvailableTimes[i-1]?.groupNum !== slot.groupNum)
                      );

                      const numVoters = slot?.voters?.filter((email) => email !== voterEmail)?.length || 0;
                      const isSelected = selectedSlot?.start?.valueOf() === slot.start?.valueOf() ||
                        (selectedSlots && selectedSlots?.find(
                          (item) => item.start.valueOf() === slot.start?.valueOf()
                        ));
                      const isBookedSlot = bookedSlotStart.valueOf() === slot.start?.valueOf();
                      const isSlotUnavailable = slot.status === AVAIL_STATUS.UNAVAILABLE;
                      return (
                        <React.Fragment key={id}>
                          {(showDstDivider) && <>
                            <TypographyBody3 sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              marginTop: '8px',
                            }}>
                              <SvgIcon
                                fontSize="inherit"
                                component={SunOutlined}
                                inheritViewBox
                                sx={{marginRight: 1, fontSize: '1rem'}}
                              />
                              {offsetDifference < 0 ? t('common.dstStarts') : t('common.dstEnds')}
                            </TypographyBody3>
                            <TypographyBody3 sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              marginBottom: '16px',
                            }}>
                              <span style={{color: theme.palette.primary.main}}>{
                                  Math.abs(offsetDifference) === HOUR_MINUTES ?
                                  t(`common.${offsetDifference < 0 ? 'jumpAheadHour' : 'fallBackHour'}`,
                                    [1]):
                                  t(`common.${offsetDifference < 0 ? 'jumpAheadMinutes' : 'fallBackMinutes'}`,
                                    [Math.abs(offsetDifference)])
                              }</span>
                            </TypographyBody3>
                          </>}
                          {(showDivider) &&
                              <SlotDivider key={id+'-divider'} />
                          }
                          <RenderSlot
                            appt={appt}
                            id={id}
                            ariaLabel={ariaLabel}
                            hasTroubleShootingMode={hasTroubleShootingMode}
                            bookedSlot={bookedSlot}
                            isBookedSlot={isBookedSlot}
                            isCrossTimeZone={crossedTimezone(slot, durationMinutes, timeZone)}
                            isSelected={isSelected}
                            isSlotUnavailable={isSlotUnavailable}
                            numVoters={numVoters}
                            onSelectSlot={onSelectSlot}
                            slot={slot}
                            timeFormat={timeFormat}
                            timeZone={timeZone}
                            troubleShootingMode={troubleShootingMode}
                            voterEmail={voterEmail}
                          />
                        </React.Fragment>
                      );
                    })
                    }
                    {isNoAvailability && <>
                      {isSmallScreen && <img
                        height="100"
                        width="100"
                        src={CalendarIllustrationGray}
                        alt="calendar"
                        style={{margin: 'auto'}}
                      />}
                      <Button variant="text" size="small" disabled sx={{
                        'fontSize': '0.75rem',
                        '&.Mui-disabled': {
                          color: zoomPalette.gray['60'],
                          fontWeight: 'normal',
                        },
                      }}>
                        {t('common.noAvailability')}
                      </Button>
                    </>}
                  </Slots>
                </Fade>
              }
            </div>
          </>
        )}
      </Stack>
    </TimeSelectSection>
  );
}

